import { inject, Injectable } from '@angular/core';
import { IAttachment } from 'app/models/support';
import { StorageUploadService } from '../storage/storage-upload.service';

const RESIZE_SUFFIX = '_2560x1440';

@Injectable({ providedIn: 'root' })
export class SupportAttachmentService {
  private uploads = inject(StorageUploadService);

  async upload(userId: string, threadId: string, file: File): Promise<IAttachment> {
    const result = await this.uploads.upload(`support/${userId}/${threadId}`, file);
    return {
      url: result.url,
      name: result.name,
      contentType: result.contentType,
      size: result.size,
      path: result.path,
    };
  }

  async uploadMany(userId: string, threadId: string, files: File[]): Promise<IAttachment[]> {
    const tasks = files.map((f) => this.upload(userId, threadId, f));
    return Promise.all(tasks);
  }

  /**
   * Delete the attachment's files from Storage. The resize extension DELETES the
   * original after resizing, so for images only the `_2560x1440` variant exists —
   * but we target both paths (delete() swallows not-found): non-image files
   * are not resized and keep the original intact. Best effort: if this fails,
   * onSupportThreadDeleted cleans up anyway when the thread is deleted.
   */
  async deleteAttachment(att: IAttachment): Promise<void> {
    const targets = new Set<string>();
    if (att.path) {
      targets.add(att.path);
      targets.add(this.withResizeSuffix(att.path));
    }
    if (att.url) {
      targets.add(att.url);
      targets.add(this.withoutResizeSuffix(att.url));
    }
    await Promise.all(
      [...targets].map((t) => this.uploads.delete(t).catch((err) =>
        console.error('[SupportAttachment] delete failed', { target: t, error: err }),
      )),
    );
  }

  private withResizeSuffix(pathOrUrl: string): string {
    const queryStart = pathOrUrl.indexOf('?');
    const head = queryStart >= 0 ? pathOrUrl.substring(0, queryStart) : pathOrUrl;
    const tail = queryStart >= 0 ? pathOrUrl.substring(queryStart) : '';
    const lastDot = head.lastIndexOf('.');
    if (lastDot < 0 || head.includes(RESIZE_SUFFIX)) return pathOrUrl;
    return head.substring(0, lastDot) + RESIZE_SUFFIX + head.substring(lastDot) + tail;
  }

  private withoutResizeSuffix(pathOrUrl: string): string {
    return pathOrUrl.replace(RESIZE_SUFFIX, '');
  }
}
